import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  CheckCircle2,
  Download,
  Pencil,
  Plus,
  Save,
  Send,
  ThumbsDown,
  X,
} from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { BackButton } from '../components/ui/BackButton';
import { Button } from '../components/ui/Button';
import { formatEUR, formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { useAuthStore } from '../stores/authStore';
import { isCustomerRole } from '../customer/shared';
import { Input } from '../components/ui/Input';
import Documents from './Documents';

interface QuoteLine {
  id: string;
  productId: string;
  productName?: string | null;
  productSku?: string | null;
  productBrand?: string | null;
  quantity: number;
  unitPriceHT: string | number;
  discountPct: string | number;
  lineTotalHT: string | number;
  source: 'INTERNAL' | 'EXTERNAL';
}

interface QuoteDetailDto {
  id: string;
  quoteNumber?: string;
  status: string;
  customerRef?: string | null;
  expiresAt?: string;
  subtotalHT?: string | number;
  vatAmount?: string | number;
  totalTTC?: string | number;
  discountPct?: string | number;
  lines?: QuoteLine[];
  convertedOrderId?: string | null;
  purchasePriceHT?: number;
  marginPct?: number;
}

// Quote status colours — the happy path (DRAFT→SENT→CONVERTED) reads green-ward,
// while the terminal non-conversions (REJECTED/EXPIRED) are visibly distinct (¶ lifecycle).
const STATUS_CLASS: Record<string, string> = {
  DRAFT: 'bg-muted text-foreground',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-success/15 text-success',
  CONVERTED: 'bg-success/25 text-success',
  REJECTED: 'bg-destructive/10 text-destructive',
  EXPIRED: 'bg-warning/20 text-warning',
};

export function QuoteStatusPill({ s }: { s: string }) {
  const cls = STATUS_CLASS[s] ?? 'bg-muted text-foreground';
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{enumLabel('quoteStatus', s)}</span>
  );
}

/**
 * Screen 23 — Quotes list.
 *
 * Renders the shared document list pinned to QUOTE, so the quotes screen gets the same free
 * search, filter bar, column set and readable dates as §4 without a second implementation to
 * keep in step. Row click still opens the quote's own detail screen.
 */
export function QuotesList() {
  return (
    <Documents
      fixedType="QUOTE"
      titleKey="documents.quotes.title"
      descriptionKey="quotes.listDescription"
    />
  );
}

const LIFECYCLE = ['DRAFT', 'SENT', 'CONVERTED'] as const;

/** Horizontal stepper DRAFT → SENT → CONVERTED (no customer accept/reject gate — a sent
 *  quote is validated straight into an order). Terminal non-conversions (REJECTED / EXPIRED)
 *  replace the tail with a distinct badge. Legacy ACCEPTED rows render as a sent quote. */
function QuoteLifecycle({ status }: { status: string }) {
  const terminalAside = status === 'REJECTED' || status === 'EXPIRED';
  const activeIdx = terminalAside
    ? 1 /* highlight up to SENT, then show the aside */
    : status === 'ACCEPTED'
      ? 1 /* legacy: treat an accepted quote as sent (awaiting conversion) */
      : LIFECYCLE.indexOf(status as (typeof LIFECYCLE)[number]);
  return (
    <div className="flex items-center gap-1 text-xs">
      {LIFECYCLE.map((step, i) => {
        const done = activeIdx >= 0 && i < activeIdx;
        const current = i === activeIdx && !terminalAside;
        return (
          <span key={step} className="flex items-center gap-1">
            {i > 0 && <span className="text-muted-foreground">→</span>}
            <span
              className={`inline-flex items-center gap-0.5 whitespace-nowrap px-2 py-0.5 rounded ${
                current
                  ? 'bg-primary text-primary-foreground font-medium'
                  : done
                    ? 'bg-success/15 text-success'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {done && <Check className="h-3 w-3 shrink-0" />}
              {enumLabel('quoteStatus', step)}
            </span>
          </span>
        );
      })}
      {terminalAside && (
        <span className="flex items-center gap-1">
          <span className="text-muted-foreground">→</span>
          <QuoteStatusPill s={status} />
        </span>
      )}
    </div>
  );
}

/** Screen 24 — Quote detail (lifecycle, PDF, send, convert). */
export function QuoteDetail() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();
  const role = useAuthStore((s) => s.user?.role);
  // No accept/reject gate: staff send the draft, then EITHER staff (at the counter) or the
  // customer (via their portal) validate the sent quote straight into an order. A customer-side
  // conversion is placed on credit and runs the B2B credit gate server-side.
  const isCustomer = isCustomerRole(role);
  const [quote, setQuote] = useState<QuoteDetailDto | null>(null);
  const [busy, setBusy] = useState(false);
  // Inline edit of a live quote (DRAFT/SENT). `draftQty` holds the pending quantity per line id;
  // a line dropped to 0 is removed on save. Null = not editing.
  const [draftQty, setDraftQty] = useState<Record<string, number> | null>(null);
  const [draftRef, setDraftRef] = useState('');

  const reload = async () => {
    try {
      const { data } = await apiClient.get<QuoteDetailDto>(`/quotes/${id}`);
      setQuote(data);
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    void reload();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate + email the PDF and move DRAFT → SENT, then reload to expose the download.
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ sentTo?: string }>(
        `/quotes/${id}/send`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: data.sentTo ? t('quotes.sentTo', { email: data.sentTo }) : t('quotes.sent'),
      });
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  // ---- inline edit (DRAFT/SENT) ------------------------------------------
  const startEdit = () => {
    setDraftQty(Object.fromEntries((quote?.lines ?? []).map((l) => [l.id, l.quantity])));
    setDraftRef(quote?.customerRef ?? '');
  };
  const cancelEdit = () => setDraftQty(null);

  const saveEdit = async () => {
    if (!draftQty || !quote) return;
    // Full replace: the server re-prices and re-reserves stock for exactly these lines.
    const lines = (quote.lines ?? [])
      .map((l) => ({ productId: l.productId, quantity: draftQty[l.id] ?? l.quantity }))
      .filter((l) => l.quantity > 0);
    if (lines.length === 0) {
      push({
        tone: 'warning',
        message: t('quotes.needsOneLine'),
      });
      return;
    }
    setBusy(true);
    try {
      await apiClient.put(
        `/quotes/${id}`,
        { lines, customerRef: draftRef.trim() || undefined },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const wasSent = quote.status === 'SENT';
      push({
        tone: 'success',
        message: wasSent ? t('quotes.updatedResend') : t('quotes.updated'),
      });
      setDraftQty(null);
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  // Customer turned the quote down — releases the stock the quote was holding.
  const decline = async () => {
    setBusy(true);
    try {
      await apiClient.post(
        `/quotes/${id}/reject`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('quotes.declined') });
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  const convert = async (acceptSupplierSwitch = false) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ orderId: string; orderNumber: string }>(
        `/quotes/${id}/convert`,
        acceptSupplierSwitch ? { acceptSupplierSwitch: true } : {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('quotes.converted', { number: data.orderNumber }),
      });
      // Land on the resulting order so the user sees the frozen quote prices.
      navigate(`/orders/${data.orderId}`);
    } catch (err) {
      /* INV-47 — a quote reserves nothing, so stock quoted from our shelf may be gone. The order
         was NOT created; taking the missing units from a supplier (a later date) needs the
         customer's say-so, asked here, and is recorded on the order when accepted. */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d = (err as any)?.response?.data;
      if (!acceptSupplierSwitch && d?.code === 'STOCK_CHANGED' && d.details) {
        // EVERY short line, not just the first: accepting switches them all, so they are all
        // named here. The switched units are re-priced at the supplier's price of today.
        const short: Array<{ productId: string; requestedQty: number; availableQty: number }> = d
          .details.lines ?? [d.details];
        const list = short
          .map((sl) => {
            const line = quote?.lines?.find((l) => l.productId === sl.productId);
            return t('quotes.stockChangedLine', {
              sku: line?.productSku ?? line?.productName ?? sl.productId,
              requested: sl.requestedQty,
              available: sl.availableQty,
              missing: Number(sl.requestedQty) - Number(sl.availableQty),
            });
          })
          .join('\n');
        const ok = window.confirm(t('quotes.stockChangedConfirm', { lines: list }));
        setBusy(false);
        if (ok) await convert(true);
        return;
      }
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  // PDF is generated live by the backend and streamed (company header + French devis).
  // Fetch as an authenticated blob and open it in a new tab.
  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await apiClient.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  if (!quote) return <Skeleton className="h-40 w-full" />;

  const lines = quote.lines ?? [];
  // Guide E2: "it locks the price for 30 days but reserves nothing; turning it into an order goes
  // through the recount again". Saying otherwise on the screen promised stock we do not hold.
  const live = quote.status === 'DRAFT' || quote.status === 'SENT' || quote.status === 'ACCEPTED';

  return (
    <div className="space-y-4">
      <BackButton to="/quotes" label={t('quotes.backToQuotes')} className="mb-0" />
      <header className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">
          {t('quotes.heading', { number: quote.quoteNumber ?? quote.id.slice(0, 8) })}
        </h1>
        <QuoteStatusPill s={quote.status} />
      </header>

      <QuoteLifecycle status={quote.status} />

      {/* A quote holds no stock: it commits a PRICE. What is on the shelf is recounted when it is
          converted, and the customer is asked before anything moves to a supplier (INV-47). */}
      {live && lines.length > 0 && (
        <div className="text-sm rounded border border-info/40 bg-info/10 px-3 py-2">
          {t('quotes.noReservationBanner')}
        </div>
      )}
      {quote.status === 'EXPIRED' && (
        <div className="text-sm rounded border border-warning/40 bg-warning/10 text-warning px-3 py-2">
          {t('quotes.expiredBanner')}
        </div>
      )}
      {quote.status === 'CONVERTED' && quote.convertedOrderId && (
        <div className="text-sm rounded border bg-card px-3 py-2">
          {t('quotes.convertedBanner')}{' '}
          <Link to={`/orders/${quote.convertedOrderId}`} className="text-primary underline">
            {t('quotes.viewOrder')}
          </Link>
          .
        </div>
      )}

      <section className="border rounded p-4 bg-card text-sm grid grid-cols-2 gap-x-6 gap-y-1 max-w-md">
        {draftQty ? (
          <>
            <span className="text-muted-foreground">{t('quotes.yourReference')}</span>
            <Input
              className="h-8"
              value={draftRef}
              maxLength={64}
              placeholder={t('quotes.customerReference')}
              onChange={(e) => setDraftRef(e.target.value)}
              aria-label={t('quotes.customerReference')}
            />
          </>
        ) : (
          quote.customerRef && (
            <>
              <span className="text-muted-foreground">{t('quotes.yourReference')}</span>
              <span>{quote.customerRef}</span>
            </>
          )
        )}
        <span className="text-muted-foreground">{t('quotes.validUntil')}</span>
        <span>{formatDate(quote.expiresAt)}</span>
        <span className="text-muted-foreground">{t('common.totalHT')}</span>
        <span>{formatEUR(quote.subtotalHT)}</span>
        <span className="text-muted-foreground">{t('common.vat')}</span>
        <span>{formatEUR(quote.vatAmount)}</span>
        <span className="text-muted-foreground font-medium">{t('common.totalTTC')}</span>
        <span className="font-medium">{formatEUR(quote.totalTTC)}</span>
      </section>

      {lines.length > 0 && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('common.lines')}</h2>
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t('common.product')}</th>
                <th>{t('common.qty')}</th>
                <th>{t('quotes.unitPriceHT')}</th>
                <th>{t('common.discount')}</th>
                <th>{t('common.totalHT')}</th>
                <th>{t('quotes.stock')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="py-1">
                    <div className="truncate max-w-[18rem]" title={l.productName ?? l.productId}>
                      {l.productName ?? l.productId}
                    </div>
                    {(l.productSku || l.productBrand) && (
                      <div className="text-xs text-muted-foreground">
                        {[l.productBrand, l.productSku].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    {draftQty ? (
                      <Input
                        type="number"
                        min={0}
                        className="h-8 w-16 px-1 text-center"
                        value={draftQty[l.id] ?? l.quantity}
                        onChange={(e) =>
                          setDraftQty((d) => ({
                            ...d,
                            [l.id]: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        aria-label={t('quotes.quantityFor', {
                          product: l.productName ?? l.productId,
                        })}
                      />
                    ) : (
                      l.quantity
                    )}
                  </td>
                  <td className="text-center">{formatEUR(l.unitPriceHT)}</td>
                  <td className="text-center">{Number(l.discountPct).toFixed(2)}%</td>
                  <td className="text-center">{formatEUR(l.lineTotalHT)}</td>
                  <td className="text-center">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${l.source === 'INTERNAL' ? 'bg-success/15 text-success' : 'bg-warning/20 text-warning'}`}
                    >
                      {l.source === 'INTERNAL' ? t('quotes.reserved') : t('quotes.onOrder')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {draftQty && (
        <div className="text-sm rounded border border-warning/40 bg-warning/10 text-warning px-3 py-2">
          {t('quotes.editingBanner')}
          {quote.status === 'SENT' && ` ${t('quotes.editingBannerSent')}`}{' '}
          {t('quotes.editingBannerDropLine')}
        </div>
      )}

      <section className="border rounded p-4 bg-card">
        <h2 className="font-semibold mb-2">{t('quotes.actions')}</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={downloadPdf}>
            <Download className="h-4 w-4" /> {t('quotes.downloadPdf')}
          </Button>

          {/* Staff send the draft. */}
          {!isCustomer && quote.status === 'DRAFT' && (
            <Button size="sm" disabled={busy} onClick={send}>
              <Send className="h-4 w-4" />
              {t('quotes.send')}
            </Button>
          )}

          {/* Re-send an already-sent quote (customer lost it / wrong address). Re-renders the
              current PDF, so it also delivers any edits made since the first send. */}
          {!isCustomer && quote.status === 'SENT' && !draftQty && (
            <Button variant="outline" size="sm" disabled={busy} onClick={send}>
              <Send className="h-4 w-4" />
              {t('quotes.resend')}
            </Button>
          )}

          {/* Edit a live quote instead of starting a new one. */}
          {!isCustomer &&
            (quote.status === 'DRAFT' || quote.status === 'SENT') &&
            (draftQty ? (
              <>
                <Button size="sm" disabled={busy} onClick={saveEdit}>
                  <Save className="h-4 w-4" />
                  {t('quotes.saveChanges')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={cancelEdit}>
                  <X className="h-4 w-4" />
                  {t('common.cancel')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" disabled={busy} onClick={startEdit}>
                  <Pencil className="h-4 w-4" />
                  {t('quotes.changeQuantities')}
                </Button>
                {/* Adding a part needs the catalogue search, which lives on the order screen —
                    so editing the BASKET reopens the quote there. Saving replaces this quote. */}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => navigate(`/orders/new?quoteId=${quote.id}`)}
                >
                  <Plus className="h-4 w-4" />
                  {t('quotes.addRemoveParts')}
                </Button>
              </>
            ))}

          {/* Customer said no — free the stock this quote is holding rather than waiting for
              the reservation to lapse. */}
          {!isCustomer && quote.status === 'SENT' && !draftQty && (
            <Button variant="outline" size="sm" disabled={busy} onClick={decline}>
              <ThumbsDown className="h-4 w-4" />
              {t('quotes.customerDeclined')}
            </Button>
          )}

          {/* Validate a sent quote straight into an order — available to staff (counter) and
              the customer (portal). Legacy ACCEPTED quotes remain convertible too. */}
          {(quote.status === 'SENT' || quote.status === 'ACCEPTED') && !draftQty && (
            <Button variant="success" size="sm" disabled={busy} onClick={() => void convert()}>
              <CheckCircle2 className="h-4 w-4" />
              {t('quotes.convertToOrder')}
            </Button>
          )}
        </div>

        {/* Tell each side what they're waiting on, so an action-less status isn't a dead end. */}
        {isCustomer && quote.status === 'DRAFT' && (
          <p className="text-xs text-muted-foreground mt-2">{t('quotes.waitingCustomer')}</p>
        )}
        {(quote.status === 'SENT' || quote.status === 'ACCEPTED') && (
          <p className="text-xs text-muted-foreground mt-2">
            {isCustomer ? t('quotes.waitingSentCustomer') : t('quotes.waitingSentStaff')}
          </p>
        )}
      </section>
    </div>
  );
}
